/**
 * Cliente HTTP do processamento automático do caso eletrônico (E89.F2, GUS-50).
 *
 * Espelha `apps/api/types/processamento_inicial.py` e o `POST
 * /processar-inicial` de `apps/api/runtime/exame_eletronico.py`. Módulo próprio
 * porque `exame_eletronico.ts` já passou do teto de linhas.
 *
 * A chamada não leva corpo, e de propósito: quem decide quais peças verificar e
 * se a primeira atualização das fontes externas é paga é o servidor, pela
 * evidência gravada (ADR-0077 §2.6). A tela só dispara e conta o que houve.
 */
import type { AxiosInstance } from 'axios';

import {
  classifyExameEletronicoError,
  ExameEletronicoError,
  type ExameEletronicoRecordData,
  type SituacaoItem,
} from '@/lib/exame_eletronico';

/** Os desfechos, na ordem do `Literal` do Python — há teste de paridade. */
export const MOTIVOS_PROCESSAMENTO_INICIAL = [
  'executado',
  'ja_processado',
  'sem_material',
  'sem_protocolo',
  'pecas_concluidas',
  'separacao_reprovada',
  'leitura_em_andamento',
  'aguardando_arquivos',
] as const;

export type MotivoProcessamentoInicial = (typeof MOTIVOS_PROCESSAMENTO_INICIAL)[number];

export interface ProcessamentoInicialData {
  executou: boolean;
  /** O que ESTA chamada fez: verdadeiro só quando pagou a primeira atualização do caso. */
  atualizou_consultas: boolean;
  motivo: MotivoProcessamentoInicial;
  /** As peças verificadas agora — vazio em todo desfecho que não executou. */
  pecas_verificadas: string[];
  exames: ExameEletronicoRecordData[];
}

/**
 * Tempo de espera desta chamada, e não o padrão de 15 s do cliente.
 *
 * Na primeira chegada o servidor consulta as fontes externas em série antes de
 * verificar as peças — cadastro (20 s), geocodificação (15 s) e classificação
 * de IP (10 s por endereço), nos `*_client.py` de `apps/api/repositories/`. Com
 * 15 s o navegador desistiria enquanto o servidor ainda grava, e a tela
 * mostraria falha de um processamento que deu certo (E82.F5).
 */
export const PROCESSAMENTO_INICIAL_TIMEOUT_MS = 120_000;

function ehMotivo(valor: unknown): valor is MotivoProcessamentoInicial {
  return (MOTIVOS_PROCESSAMENTO_INICIAL as readonly unknown[]).includes(valor);
}

/**
 * A resposta tem a forma do contrato?
 *
 * Sem esta conferência, o 204 vazio do mock genérico dos specs de outras Fases
 * chegaria como `''`, e a leitura de `pecas_verificadas.length` derrubaria a
 * página inteira em vez de virar uma falha contida, com frase própria.
 */
function temForma(dados: unknown): dados is ProcessamentoInicialData {
  if (typeof dados !== 'object' || dados === null) return false;
  const d = dados as Record<string, unknown>;
  return (
    typeof d.executou === 'boolean' &&
    typeof d.atualizou_consultas === 'boolean' &&
    ehMotivo(d.motivo) &&
    Array.isArray(d.pecas_verificadas) &&
    Array.isArray(d.exames)
  );
}

export async function processarInicial(
  client: AxiosInstance,
  caseId: string,
): Promise<ProcessamentoInicialData> {
  let dados: unknown;
  try {
    const resposta = await client.post<unknown>(
      `/casos/${caseId}/exame-eletronico/processar-inicial`,
      undefined,
      { timeout: PROCESSAMENTO_INICIAL_TIMEOUT_MS },
    );
    dados = resposta.data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  if (!temForma(dados)) throw new ExameEletronicoError('unknown');
  return dados;
}

// O que as consultas externas de fato fizeram (E91.F3, GUS-51 RF09) ----------

/** As fontes com provedor externo, na ordem em que a tela as lista. */
export const FONTES_CONSULTADAS = [
  'cluster_cadastral',
  'geocodificacao',
  'classificacao_ip',
] as const;

export type FonteConsultada = (typeof FONTES_CONSULTADAS)[number];

/**
 * - `executada`: o estágio rodou e devolveu (`confere`).
 * - `indisponivel`: o estágio não rodou (`nao_executado`: sem chave configurada,
 *   provedor sem resposta, sem CPF).
 * - `sem_dado`: rodou até onde havia insumo e não havia (`ausente`: sem
 *   endereços a localizar, sem endereço de rede no protocolo).
 * - `nao_apurada`: nenhum exame verificado trouxe o estágio.
 */
export type EstadoDaConsulta = 'executada' | 'indisponivel' | 'sem_dado' | 'nao_apurada';

export interface ResumoDasConsultas {
  porFonte: Record<FonteConsultada, EstadoDaConsulta>;
  /** As fontes que ficaram indisponíveis ou sem dado, na ordem da lista. */
  pendentes: FonteConsultada[];
}

function estadoDaFonte(situacoes: readonly SituacaoItem[]): EstadoDaConsulta {
  if (situacoes.length === 0) return 'nao_apurada';
  // Uma peça sem consulta basta para a fonte não ter sido consultada para o
  // caso inteiro: o resumo é conservador de propósito.
  if (situacoes.includes('nao_executado')) return 'indisponivel';
  if (situacoes.includes('confere') || situacoes.includes('nao_confere')) return 'executada';
  return 'sem_dado';
}

/**
 * Lê os itens de enriquecimento dos quadros devolvidos e diz, por fonte, o que
 * houve. NUNCA usa o `detalhe` do servidor — ele nomeia serviço e configuração
 * ("sem chave do bureau"), e isso não é frase para quem consulta o caso.
 */
export function resumoDasConsultas(
  exames: readonly ExameEletronicoRecordData[],
): ResumoDasConsultas {
  const porFonte = Object.fromEntries(
    FONTES_CONSULTADAS.map((fonte) => {
      const situacoes = exames.flatMap((exame) =>
        (exame.resultado?.enriquecimento ?? [])
          .filter((item) => item.chave === fonte)
          .map((item) => item.situacao),
      );
      return [fonte, estadoDaFonte(situacoes)];
    }),
  ) as Record<FonteConsultada, EstadoDaConsulta>;
  const pendentes = FONTES_CONSULTADAS.filter(
    (fonte) => porFonte[fonte] === 'indisponivel' || porFonte[fonte] === 'sem_dado',
  );
  return { porFonte, pendentes };
}
